import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import API_BASE_URL from '../config/api';
import './AuthCard.css';

/**
 * Self-service password reset page.
 *
 * Two modes, driven by the URL:
 *  - /reset-password                     -> request a reset link by email
 *  - /reset-password?user=...&token=...  -> set a new password (link from email)
 *
 * Shares AuthCard.css with the login popup and the change-password modal so
 * every auth dialogue matches the landing page.
 */
const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const tokenUser = searchParams.get('user') || '';
  const token = searchParams.get('token') || '';
  const hasToken = Boolean(tokenUser && token);

  // Request-link mode
  const [email, setEmail] = useState('');
  const [requestSent, setRequestSent] = useState(false);

  // Set-password mode
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetDone, setResetDone] = useState(false);

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleRequestLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: email }),
      });
      // Backend always answers generically; treat any response as sent
      setRequestSent(true);
    } catch (err) {
      setError('Nätverksfel - försök igen.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Lösenorden matchar inte');
      return;
    }
    if (newPassword.length < 8) {
      setError('Lösenordet måste vara minst 8 tecken');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: tokenUser, token, newPassword }),
      });
      const data = await response.json();
      if (response.ok) {
        setResetDone(true);
      } else {
        setError(data.error || 'Kunde inte återställa lösenordet');
      }
    } catch (err) {
      setError('Nätverksfel - försök igen.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <svg className="auth-mark" viewBox="0 0 34 28" aria-hidden="true">
          <ellipse cx="12" cy="14" rx="10" ry="11" fill="#1B5E5A" opacity="0.88" />
          <ellipse cx="21" cy="11" rx="10" ry="11" fill="#C2683E" opacity="0.82" />
          <ellipse cx="17" cy="18" rx="9.5" ry="10" fill="#5B8C5A" opacity="0.82" />
        </svg>

        <div className="auth-eyebrow">Hälsosam Urbanism</div>
        <h1 className="auth-title">
          {hasToken ? 'Välj ett nytt lösenord' : 'Återställ ditt lösenord'}
        </h1>

        {!hasToken && !requestSent && (
          <>
            <p className="auth-lede">
              Ange din e-postadress (ditt användarnamn) så skickar vi en länk för att
              återställa lösenordet.
            </p>
            <form onSubmit={handleRequestLink} className="auth-form">
              {error && <div className="auth-error">{error}</div>}
              <label className="auth-field">
                <span className="auth-label">E-postadress</span>
                <input
                  type="email"
                  placeholder="namn@exempel.se"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  required
                />
              </label>
              <div className="auth-actions">
                <button type="submit" className="auth-submit" disabled={isLoading}>
                  {isLoading ? 'Skickar…' : 'Skicka återställningslänk'}
                </button>
              </div>
            </form>
          </>
        )}

        {!hasToken && requestSent && (
          <div className="auth-success">
            Om kontot finns har ett e-postmeddelande med en återställningslänk skickats
            till <strong>{email}</strong>. Länken är giltig i 60 minuter.
          </div>
        )}

        {hasToken && !resetDone && (
          <>
            <p className="auth-lede">
              Nytt lösenord för <strong>{tokenUser}</strong>.
            </p>
            <form onSubmit={handleSetPassword} className="auth-form">
              {error && <div className="auth-error">{error}</div>}
              <label className="auth-field">
                <span className="auth-label">Nytt lösenord</span>
                <input
                  type="password"
                  placeholder="Minst 8 tecken"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
              </label>
              <label className="auth-field">
                <span className="auth-label">Bekräfta nytt lösenord</span>
                <input
                  type="password"
                  placeholder="Upprepa nytt lösenord"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
              </label>
              <div className="auth-actions">
                <button type="submit" className="auth-submit" disabled={isLoading}>
                  {isLoading ? 'Sparar…' : 'Spara nytt lösenord'}
                </button>
              </div>
            </form>
          </>
        )}

        {hasToken && resetDone && (
          <div className="auth-success">
            Lösenordet har återställts. Du kan nu{' '}
            <Link to="/?login=1">logga in</Link> med ditt nya lösenord.
          </div>
        )}

        <Link to="/?login=1" className="auth-link">Tillbaka till inloggningen</Link>
      </div>
    </div>
  );
};

export default ResetPassword;
